import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Button, Container } from '@mui/material';
import { CalendarToday, Business as BusinessIcon } from '@mui/icons-material';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';
import { createCommonStyles } from '../theme/styles';
import { setDemoMode } from '../utils/demoMode';
import { useDispatch } from 'react-redux';
import { setDemoUser } from '../store/slices/authSlice';
import { mockUser, mockTenantAdminUser } from '../services/mockData';
import { darkColors, lightColors } from '../theme';
import Logo from '../assets/ZakazujMeLogo4.png';

const DemoLanding = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isDarkMode } = useAppTheme();
  const commonStyles = createCommonStyles(isDarkMode);
  const customColors = isDarkMode ? darkColors : lightColors;

  const handleRoleSelect = (role) => {
    setDemoMode(true, role);
    
    // Postavi odgovarajućeg demo user-a u Redux store
    if (role === 'customer') {
      dispatch(setDemoUser(mockUser));
      navigate('/demo/last-and-next-appointment');
    } else {
      // owner = tenant_admin
      dispatch(setDemoUser(mockTenantAdminUser));
      navigate('/demo/live-dashboard');
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 8 }, px: { xs: 2, md: 3 } }}>
      {/* Logo */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 2, md: 4 } }}>
        <Box
          component="img"
          src={Logo}
          alt="Zakazuj Logo"
          sx={{
            height: { xs: 50, md: 80 },
            width: 'auto',
            objectFit: 'contain',
          }}
        />
      </Box>

      <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 6 } }}>
        <Typography
          variant="h6"
          sx={{ ...commonStyles.textSecondary, mb: 1, fontSize: { xs: '1rem', md: '1.25rem' } }}
        >
          Demo verzija - bez registracije
        </Typography>
        <Typography
          variant="body2"
          sx={{ ...commonStyles.textSecondary, fontSize: { xs: '0.75rem', md: '0.875rem' } }}
        >
          Ovo je demo prikaz - podaci nisu stvarni
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 2, md: 3 },
          mt: { xs: 1, md: 4 },
        }}
      >
        {/* Kartica za korisnika */}
        <Card
          sx={{
            background: isDarkMode
              ? 'rgba(30, 41, 59, 0.8)'
              : 'rgba(255, 255, 255, 1)',
            border: isDarkMode
              ? '1px solid rgba(148, 163, 184, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: 3,
            boxShadow: isDarkMode
              ? '0 4px 12px rgba(0, 0, 0, 0.3)'
              : '0 4px 12px rgba(0, 0, 0, 0.1)',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: isDarkMode
                ? '0 8px 24px rgba(0, 0, 0, 0.4)'
                : '0 8px 24px rgba(0, 0, 0, 0.15)',
            },
          }}
          onClick={() => handleRoleSelect('customer')}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
            <Box
              sx={{
                width: { xs: 60, md: 80 },
                height: { xs: 60, md: 80 },
                borderRadius: '50%',
                background: customColors.gradient.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: { xs: 2, md: 3 },
                boxShadow: `0 8px 16px ${customColors.primary.main}50`,
              }}
            >
              <CalendarToday
                sx={{ color: 'white', fontSize: { xs: 30, md: 40 } }}
              />
            </Box>
            <Typography
              variant="h5"
              sx={{ ...commonStyles.textPrimary, fontWeight: 600, mb: { xs: 1.5, md: 2 }, fontSize: { xs: '1.25rem', md: '1.5rem' } }}
            >
              Ja sam korisnik
            </Typography>
            <Typography
              variant="body2"
              sx={{ ...commonStyles.textSecondary, mb: { xs: 2, md: 3 }, fontSize: { xs: '0.875rem', md: '1rem' } }}
            >
              Želim da zakažem termin
            </Typography>
            <Button
              variant="contained"
              fullWidth
              sx={{
                ...commonStyles.gradientButton,
                py: { xs: 1.25, md: 1.5 },
                fontSize: { xs: '0.875rem', md: '1rem' },
              }}
            >
              Pokreni demo
            </Button>
          </CardContent>
        </Card>

        {/* Kartica za vlasnika */}
        <Card
          sx={{
            background: isDarkMode
              ? 'rgba(30, 41, 59, 0.8)'
              : 'rgba(255, 255, 255, 1)',
            border: isDarkMode
              ? '1px solid rgba(148, 163, 184, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: 3,
            boxShadow: isDarkMode
              ? '0 4px 12px rgba(0, 0, 0, 0.3)'
              : '0 4px 12px rgba(0, 0, 0, 0.1)',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: isDarkMode
                ? '0 8px 24px rgba(0, 0, 0, 0.4)'
                : '0 8px 24px rgba(0, 0, 0, 0.15)',
            },
          }}
          onClick={() => handleRoleSelect('owner')}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
            <Box
              sx={{
                width: { xs: 60, md: 80 },
                height: { xs: 60, md: 80 },
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${customColors.secondary.main} 0%, ${customColors.secondary.light} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: { xs: 2, md: 3 },
                boxShadow: `0 8px 16px ${customColors.secondary.main}50`,
              }}
            >
              <BusinessIcon
                sx={{ color: 'white', fontSize: { xs: 30, md: 40 } }}
              />
            </Box>
            <Typography
              variant="h5"
              sx={{ ...commonStyles.textPrimary, fontWeight: 600, mb: { xs: 1.5, md: 2 }, fontSize: { xs: '1.25rem', md: '1.5rem' } }}
            >
              Ja sam vlasnik radnje
            </Typography>
            <Typography
              variant="body2"
              sx={{ ...commonStyles.textSecondary, mb: { xs: 2, md: 3 }, fontSize: { xs: '0.875rem', md: '1rem' } }}
            >
              Želim da vodim termine
            </Typography>
            <Button
              variant="contained"
              fullWidth
              sx={{
                background: `linear-gradient(135deg, ${customColors.secondary.main} 0%, ${customColors.secondary.light} 100%)`,
                color: 'white',
                fontWeight: 600,
                py: { xs: 1.25, md: 1.5 },
                fontSize: { xs: '0.875rem', md: '1rem' },
                '&:hover': {
                  background: `linear-gradient(135deg, ${customColors.secondary.light} 0%, ${customColors.secondary.main} 100%)`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 20px ${customColors.secondary.main}50`,
                },
              }}
            >
              Pokreni demo
            </Button>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default DemoLanding;
